<template>
	<div class="action-ribbon" data-id="actionRibbon">
		<div class="action-ribbon__left-section" data-testid="left-section">
			<template v-for="leftButton in buttonsObject.leftButtons">
				<component
					:key="
						`${leftButton.location}${leftButton.options.labelText}${leftButton.options.iconClassName}`
					"
					v-bind:is="leftButton.buttonType"
					v-if="leftButton.visible"
					v-bind="leftButton.options"
					@click="
						opts => {
							clickHandler(opts, leftButton.options.clickHandler);
						}
					"
					@optionClick="
						opts => {
							clickHandler(
								opts,
								leftButton.options.optionClickHandler
							);
						}
					"
					:displayOnActiveFeatureSwitches="
						leftButton.displayOnActiveFeatureSwitches
					"
					:ref="leftButton.ref"
				/>
				<Divider
					v-if="leftButton.divider"
					:key="
						`Divider_${leftButton.location}${leftButton.options.labelText}${leftButton.options.iconClassName}`
					"
					variant="vertical"
				/>
			</template>
		</div>
		<div class="action-ribbon__right-section" data-testid="right-section">
			<template v-for="rightButton in buttonsObject.rightButtons">
				<component
					:key="
						`${rightButton.location}${rightButton.options.labelText}${rightButton.options.iconClassName}`
					"
					v-bind:is="rightButton.buttonType"
					v-if="rightButton.visible"
					v-bind="rightButton.options"
					@click="
						opts => {
							clickHandler(
								opts,
								rightButton.options.clickHandler
							);
						}
					"
					@optionClick="
						opts => {
							clickHandler(
								opts,
								rightButton.options.optionClickHandler
							);
						}
					"
					:displayOnActiveFeatureSwitches="
						rightButton.displayOnActiveFeatureSwitches
					"
				/>
				<Divider
					v-if="rightButton.divider"
					:key="
						`Divider_${rightButton.location}${rightButton.options.labelText}${rightButton.options.iconClassName}`
					"
					variant="vertical"
				/>
			</template>

			<ButtonActionRibbonMenuMore
				v-if="hasMoreButtons"
				:options="buttonsObject.moreButtons"
			/>
		</div>
	</div>
</template>

<script>
import ButtonActionRibbon from '@/components/forms/button/button-action-ribbon.vue';
import ButtonActionRibbonMenu from '@/components/forms/button/button-action-ribbon-menu.vue';
import ButtonActionRibbonMenuMore from '@/components/forms/button/button-action-ribbon-menu-more.vue';
import ButtonActionRibbonSplit from '@/components/forms/button/button-action-ribbon-split.vue';
import Divider from '@/components/visual/divider/divider.vue';
import { BUTTON_ACTION_RIBBON_SPLIT } from '@/components/shells/action-ribbon/action-ribbon-button-types';
import { cloneDeep } from 'lodash';

export default {
	name: 'ActionRibbon',
	components: {
		ButtonActionRibbon,
		ButtonActionRibbonMenu,
		ButtonActionRibbonMenuMore,
		ButtonActionRibbonSplit,
		Divider
	},
	data() {
		return {
			windowWidth: undefined
		};
	},
	mounted() {
		this.setWidth();
		window.addEventListener('resize', this.setWidth);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.setWidth);
	},
	props: {
		maxTabletButtons: {
			type: Number,
			default: 2
		},
		/**
		 * An array of objects, defined as follows
		 * @property {String} location  // Button placement - left, right or more
		 * @property {string} buttonType // An action ribbon button type from action-ribbon-button-types
		 * @property {boolean} visible // Show/hide button
		 * @property {boolean} divider // Show divider after button
		 * @property {Object} options // Properties/Attributes to be passed to the button.  Includes these click Handlers:
		 * 								clickHandler: function to handle click events,
		 * 								optionClickHandler: function to handle optionClick for ButtonActionRibbonSplit
		 */
		buttonList: {
			type: Array,
			default: () => []
		}
	},
	computed: {
		isTabletMode() {
			return this.windowWidth <= 1200;
		},
		hasMoreButtons() {
			return this.buttonsObject.moreButtons.length > 0;
		},
		buttonsObject() {
			let buttonList = [
				...this.buttonList.map(object => cloneDeep(object))
			];
			buttonList = buttonList.filter(
				x => x.visible === undefined || x.visible === true
			);
			let mainButtons = buttonList.filter(
				b => b.location == 'left' || b.location == 'right'
			);
			let moreButtons = buttonList.filter(b => b.location == 'more');
			let leftButtons = [];
			let rightButtons = [];
			let tabletButtons = [];

			if (this.isTabletMode) {
				tabletButtons = mainButtons.slice(0, this.maxTabletButtons);
				leftButtons = tabletButtons.filter(b => b.location == 'left');
				rightButtons = tabletButtons.filter(b => b.location == 'right');
				moreButtons.unshift(
					...mainButtons.slice(this.maxTabletButtons)
				);
			} else {
				leftButtons = buttonList.filter(b => b.location == 'left');
				rightButtons = buttonList.filter(b => b.location == 'right');
			}

			moreButtons.forEach(btn => {
				btn.options.text = btn.options.text || btn.options.labelText;
				btn.options.value = btn.options.value || btn.options.labelText;
				btn.options.iconClass = btn.options.iconClass || null;
				// Add the default action in a split button as an option in the more menu
				if (btn.buttonType === BUTTON_ACTION_RIBBON_SPLIT) {
					// set clickHandler for existing options
					btn.options.options.forEach(btnOpt => {
						btnOpt.clickHandler = btn.options.optionClickHandler;
					});

					let defaultAction = {
						text: btn.options.labelText,
						value: btn.options.labelText
					};
					btn.options.options.push(defaultAction);
				}
			});

			return {
				leftButtons: leftButtons,
				rightButtons: rightButtons,
				moreButtons: moreButtons.map(o => o.options)
			};
		}
	},
	methods: {
		setWidth() {
			this.windowWidth = window.innerWidth;
		},
		clickHandler(option, onClick) {
			if (onClick) {
				onClick(option);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.action-ribbon {
	position: fixed;
	top: 86px;
	width: 100%;
	max-width: 1354px;
	height: 70px;
	z-index: $z-level1-4;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: $padding-md;
	// "Hex value corresponds to v2 brand-2 value.
	//This value is not represented in the v1 color variables.
	border-bottom: 2px solid #136a3e;

	@include mq($from: md-l) {
		width: calc(100% - 48px); //48px is the width of the side Bar
	}

	&__left-section,
	&__right-section {
		display: flex;
		align-items: center;
	}

	&__left-section {
		& > *,
		:deep(.divider) {
			margin-right: $gutter-xl;
		}
	}

	&__right-section {
		& > *,
		:deep(.divider) {
			margin-left: $gutter-xl;
		}
	}

	:deep(.divider) {
		align-self: stretch;
		background-color: var(--neutral-2);
	}
}

/* LL styles v1 Only */
.action-ribbon {
	background-color: var(--bg-2);
}

/* LL styles v2 Only */
/* .action-ribbon {
	background-color: var(--bg-brand-2);
	border: none;
} */
</style>
